import { NgIf, NgStyle } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { SiteSvgsComponent } from '@curbnturf/custom-svgs/src/lib/site-svgs/site-svgs.component';
import {
  DEFAULT_IMAGE_URL,
  IMapPoint,
  IRoutePoint,
  IUserCustomLocation,
  ListingPoint,
  MapPointTypes,
  User,
} from '@curbnturf/entities';
import { ListingSearchFacade } from '@curbnturf/listing-search/src/lib/+state/listing-search.facade';
import { TripPlannerFacade } from '@curbnturf/trip-planner/src/lib/+state/trip-planner.facade';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { AlertController, IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'curbnturf-user-point-card',
  templateUrl: './user-point-card.component.html',
  styleUrls: ['./user-point-card.component.scss'],
  imports: [NgIf, NgStyle, IonicModule, SiteSvgsComponent],
})
export class UserPointCardComponent implements OnChanges, OnInit, OnDestroy {
  @Input() userPoint?: IMapPoint;
  @Output() closeBubble: EventEmitter<void> = new EventEmitter<void>();

  @Output() userPointUpdated: EventEmitter<IUserCustomLocation> = new EventEmitter<IUserCustomLocation>();
  @Output() userPointRemoved: EventEmitter<IUserCustomLocation> = new EventEmitter<IUserCustomLocation>();

  name: string;

  photoUrl: string = '';

  user: User;

  subscriptions: Subscription[] = [];

  constructor(
    private alertController: AlertController,
    private cd: ChangeDetectorRef,
    private listingSearchFacade: ListingSearchFacade,
    private tripPlannerFacade: TripPlannerFacade,
    private userFacade: UserFacade,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.userFacade.currentUser$.subscribe((user) => {
        this.user = user;
        this.cd.detectChanges();
      }),
    );
  }

  ngOnChanges() {
    if (this.userPoint) {
      this.photoUrl = DEFAULT_IMAGE_URL;
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  onAddToTrip(event: Event) {
    event.stopPropagation();
    if (this.userPoint?.coordinates) {
      const point: IRoutePoint = {
        isUser: true,
        user: this.userPoint,
        name: this.userPoint.name,
        coordinates: this.userPoint.coordinates,
        isActive: true,
      };
      this.tripPlannerFacade.addPoint(point, true);
    }
    this.closeBubble.emit();
  }

  async onRemove() {
    const alert = await this.alertController.create({
      header: 'Are you sure you want to remove this location?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Yes, Remove',
          role: 'confirm',
          handler: () => {
            if (this.userPoint?.id) {
              this.listingSearchFacade.removeUserPoint(this.userPoint);
              this.userPointRemoved.emit(this.userPoint);
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async onEdit() {
    const alert = await this.alertController.create({
      header: 'Add Custom Location',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Save',
          role: 'submit',
          handler: (value) => {
            if (this.userPoint) {
              const point = new ListingPoint({
                ...this.userPoint,
                id: this.userPoint.id || 0,
                name: value.name,
                photos: [],
              });
              this.listingSearchFacade.updateUserPoint(point);
              this.userPointUpdated.emit(point);
            }
          },
        },
      ],
      inputs: [
        {
          name: 'name',
          value: this.userPoint?.name,
          placeholder: 'Location Name',
          attributes: {
            maxlength: 50,
          },
        },
      ],
    });

    await alert.present();
  }
}
